import { z } from "zod";

/**
 * Gemeinsame Bausteine für alle bidi-handel-Pipeline-Phasen.
 *
 * Drei Score-Achsen werden NIE zu einer Confidence gemerged:
 *  - matchScore: Übereinstimmung Position ↔ Kundenartikel
 *  - langtextScore: Vollständigkeit/Qualität des Langtexts
 *  - freigabeScore: Bereitschaft zur Freigabe
 * (Pattern aus bidi_LV übernommen.)
 */

/** Score 0–100 (Prozent). */
export const ScoreSchema = z.number().min(0).max(100);

/** Welcher Pipeline-Pfad? A = Kunden-LV vorhanden, B = nur Pläne. */
export const PfadSchema = z.enum(["A", "B"]);
export type Pfad = z.infer<typeof PfadSchema>;

/** Status-Übergänge der Anfrage (State-Machine im Backend validiert). */
export const AnfrageStatusSchema = z.enum([
  "eingegangen",
  "in_pruefung",
  "angebot_erstellt",
  "versendet",
  "gewonnen",
  "verloren",
  "verworfen",
]);
export type AnfrageStatus = z.infer<typeof AnfrageStatusSchema>;

/** Klärpunkt — ein offener Punkt, der menschliche Klärung braucht. */
export const KlaerpunktSchema = z.object({
  id: z.string(),
  thema: z.string(),
  beschreibung: z.string(),
  prioritaet: z.enum(["hoch", "mittel", "niedrig"]).default("mittel"),
  resolved: z.boolean().default(false),
  resolution: z.string().optional(),
  /** Erzeugende Pipeline-Phase (2/4/5). Erlaubt mergeKlaerpunkte beim Re-Run,
   *  die eigenen Punkte dieser Phase zu ERSETZEN statt anzuhängen (läuft die
   *  Phase jetzt fehlerfrei, verschwinden ihre alten Fehler). Optional —
   *  Altbestand ohne Stempel bleibt erhalten und wird per id-Dedupe abgeräumt. */
  quelle: z.number().int().optional(),
});
export type Klaerpunkt = z.infer<typeof KlaerpunktSchema>;

/** Begründung mit Score-Achse-Hinweis (welche Achse gemeint ist). */
export const BegruendungSchema = z.object({
  text: z.string(),
  achse: z.enum(["match", "langtext", "freigabe"]).optional(),
  score: ScoreSchema.optional(),
});

/**
 * PDF-Klassifikation, die beim Upload pro PDF-Datei ermittelt wird
 * (Sprint G6a). Inline-Definition statt aus pdf/classify-pdf.ts geladen,
 * damit das Schema im Anfrage-Phase-0 mitwandern kann ohne Zirkular-Import.
 */
export const PdfTypSchema = z.enum(["lv", "bauplan", "unklar"]);
export type PdfTyp = z.infer<typeof PdfTypSchema>;

export const PdfKlassifikationSchema = z.object({
  erkanntAls: PdfTypSchema,
  confidence: z.number().int().min(0).max(100),
  hinweise: z.array(z.string()).default([]),
  bestaetigt: z.boolean().default(false),
  userOverride: PdfTypSchema.optional(),
});
export type PdfKlassifikation = z.infer<typeof PdfKlassifikationSchema>;

/**
 * Kostenarten der Aufgliederung (EFB-223-Erweiterung um „Fremd").
 *
 * Vier amtliche Spalten (Lohn / Material / Geraet / Sonstige) + Fremd als
 * separate fuenfte Spalte fuer Nachunternehmer-Leistungen. Wird in der
 * Schnellkalkulation pro Komponentenzeile vergeben und im Bepreisungs-Agent
 * pro Position aggregiert (`anteilLohn` … `anteilFremd` auf der
 * PreisPosition).
 */
export const KostenartSchema = z.enum([
  "lohn",
  "material",
  "geraet",
  "sonstige",
  "fremd",
]);
export type Kostenart = z.infer<typeof KostenartSchema>;

/**
 * Eine Zeile in der Schnellkalkulation einer LV-Position. Drei Quellen:
 *  - `stammartikel`: Eintrag aus den Tenant-Kundenartikeln, Preis kommt
 *    aus der Bepreisungs-Logik (Liste × Rabatt × Marge oder Sonderpreis),
 *    ist aber inline manuell ueberschreibbar.
 *  - `hilfsartikel`: Eintrag aus `hilfsartikel.json` — Lohn-, Geraet- oder
 *    Sonstiges-Vorlage, wiederverwendbar ueber Anfragen hinweg.
 *  - `frei`: Ad-hoc-Zeile ohne Stammdaten-Bindung. Bezeichnung + Preis tippt
 *    der Innendienst frei ein. Optional via Button als Hilfsartikel
 *    persistieren.
 *
 * `einzelpreis` ist der Verkaufspreis pro Einheit inkl. Marge — der Innen-
 * dienst gibt im Dialog ein, was er dem Kunden in Rechnung stellen wuerde.
 */
export const KomponentenZeileSchema = z.object({
  id: z.string(),
  quelle: z.enum(["stammartikel", "hilfsartikel", "frei"]),
  artikelCode: z.string().nullable().default(null),
  bezeichnung: z.string().min(1),
  menge: z.number().positive(),
  einheit: z.string().min(1),
  einzelpreis: z.number().nonnegative(),
  kostenart: KostenartSchema,
  bemerkung: z.string().optional(),
  /**
   * Wenn `true`, leitet der Bepreisungs-Agent den `einzelpreis` dieser Zeile
   * bei jeder Bepreisung LIVE aus dem referenzierten Artikel (`artikelCode`)
   * ab (Listenpreis × Kondition × Marge) statt den inline gespeicherten Wert
   * zu nutzen. Gesetzt von der Stücklisten-Expansion
   * (`buildSchnellkalkulationFromArtikel`), damit Set-Preise nie veralten.
   * Default `false` = klassisches Verhalten (inline-Preis, manuell gepflegt).
   * Nur für `quelle="stammartikel"` mit gesetztem `artikelCode` wirksam.
   */
  preisAuto: z.boolean().default(false),
});
export type KomponentenZeile = z.infer<typeof KomponentenZeileSchema>;

/**
 * Eine Komponente einer Artikel-Stückliste (Montage-Stückliste / Set).
 *
 * Im Gegensatz zur `KomponentenZeile` (= aufgelöste Schnellkalkulations-Zeile)
 * ist dies die am Artikelstamm gepflegte VORLAGE: `menge` ist ein Faktor pro
 * 1 Einheit des Set-Artikels, ein Preis wird NICHT gespeichert — er entsteht
 * live aus dem referenzierten Artikel (`artikelCode`) bei der Bepreisung. Freie
 * Zeilen (`artikelCode: null`) tragen ihren Preis erst in der Schnellkalkulation.
 */
export const StuecklisteKomponenteSchema = z.object({
  /** Verweis auf einen anderen Artikelstamm-Code (Komponente) oder `null`
   *  für eine freie, noch zu kalkulierende Zeile. */
  artikelCode: z.string().nullable().default(null),
  bezeichnung: z.string().min(1),
  /** Menge pro 1 Einheit des Set-Artikels (Faktor). */
  menge: z.number().positive(),
  einheit: z.string().min(1),
  kostenart: KostenartSchema,
  /** VK pro Einheit NUR für freie Zeilen (`artikelCode: null`, z.B. Montage-
   *  Lohn). Bei gesetztem `artikelCode` ignoriert — der Preis entsteht live
   *  aus dem referenzierten Artikel. Default 0 (= „noch zu kalkulieren"). */
  einzelpreis: z.number().nonnegative().default(0),
  bemerkung: z.string().optional(),
});
export type StuecklisteKomponente = z.infer<typeof StuecklisteKomponenteSchema>;

/**
 * Artikel-Stückliste — die Komponenten (mit Mengen-Faktoren), aus denen ein
 * Set-/Montage-Artikel besteht, plus die Preis-Bildung des Sets.
 *
 * Wird am Artikelstamm (`GlobalArtikel`/`Kundenartikel`) gepflegt. Ist ein
 * solcher Set-Artikel der gewählte Artikel einer LV-/Konstruktions-Position,
 * EXPANDIERT die Match-Phase die Stückliste zur `Schnellkalkulation`
 * (= Inhalt der Schnellkalkulation = Inhalt der Stückliste).
 *
 * `preisModus`:
 *  - `summiert` (Default): Setpreis = Σ der (live bepreisten) Komponenten.
 *  - `komplettpreis`: fester Setpreis pro Einheit (`komplettpreis`); die
 *    Komponenten dienen nur der EFB-223-Aufgliederung (proportional skaliert,
 *    sodass Σ Anteile == Setpreis).
 */
export const StuecklisteSchema = z
  .object({
    komponenten: z.array(StuecklisteKomponenteSchema).min(1),
    preisModus: z.enum(["summiert", "komplettpreis"]).default("summiert"),
    /** Fester Set-Einzelpreis (VK pro 1 Einheit des Set-Artikels). Nur bei
     *  `preisModus="komplettpreis"` relevant. */
    komplettpreis: z.number().nonnegative().optional(),
    notiz: z.string().optional(),
  })
  .refine(
    (v) => v.preisModus !== "komplettpreis" || v.komplettpreis !== undefined,
    "preisModus='komplettpreis' erfordert ein gesetztes komplettpreis-Feld"
  );
export type Stueckliste = z.infer<typeof StuecklisteSchema>;

/** Eindeutige Datei-Referenz innerhalb einer Anfrage. */
export const DateiRefSchema = z.object({
  relPath: z.string(),
  mimeType: z.string(),
  groesseBytes: z.number().int().nonnegative(),
  sha256: z.string(),
  /** Klassifikation bei PDF-Dateien — gesetzt vom Upload-Handler (G6a).
   *  Optional weil GAEB-XML/Excel keine Klassifikation brauchen. */
  pdfKlassifikation: PdfKlassifikationSchema.optional(),
});
export type DateiRef = z.infer<typeof DateiRefSchema>;
